/**
 * State packs (owner, 2026-10-02: "Is it portable for all states including copy? Let's do Nevada next").
 *
 * One pack per supported state (ut.ts, nv.ts). A pack holds everything that differs by state: which Food Code the state
 * follows and how it cites it, the agencies and their sourced phone numbers, the counties and who inspects food in each,
 * food handler and manager rules, the sick-worker reporting line, workplace safety (state OSHA plan or federal), food
 * trucks, home kitchens, required signs, inspection scoring, and the plain-words fragments the app says. The local layer
 * (county / health district overrides) is the generalized `localrules-ut.ts` pattern: cells per department and topic.
 *
 * Truth rules (docs/STANDARDS.md S1, the owner's "state then county" rule):
 *  - every fact carries a citation, a URL and the day it was last checked; a quote is verbatim from the saved source text
 *    (Utah: docs/source/research/*; Nevada: docs/source/research/nevada-excerpts.txt — a test checks each quote);
 *  - a fact we could not verify is `unknown`, with what was tried. It is never guessed and never shown as a rule;
 *  - IP location never chooses a rule. The chosen state and county do (docs/STATES.md).
 */
import type { IsoDate, PermitType } from "../model.ts";

/** The states CleanCleaver has a pack for. Adding one = a new pack file + its research doc; the type system lists every slot. */
export type UsState = "UT" | "NV" | "CA" | "NY" | "OR" | "FL" | "TX" | "MA";
export const SUPPORTED_STATES: readonly UsState[] = ["UT", "NV", "CA", "NY", "OR", "FL", "TX", "MA"];
/** The names and slugs of all eight states, always in the main chunk (generated from the packs: bundles/eager-facts.ts). */
export { STATE_INDEX } from "./bundles/eager-facts.ts";
export const isSupportedState = (s: unknown): s is UsState => typeof s === "string" && (SUPPORTED_STATES as readonly string[]).includes(s);

/** Where a fact comes from. `quote` is verbatim (whitespace folded) from the saved source text. */
export interface Source {
  citation: string;
  url: string;
  quote?: string;
  lastChecked: IsoDate;
}

/** A fact that was read and verified, or one we could not verify (never shown as a rule). */
export type Fact<T> =
  | ({ status: "verified"; source: Source } & T)
  | { status: "unknown"; tried: string; lastChecked: IsoDate };

export interface Phone {
  label: string;
  /** Exactly as the source prints it. */
  number: string;
  source: Source;
}

export interface Agency {
  id: string;
  name: string;
  /** Short name for one plain line ("Utah OSHA (UOSH)", "SNHD"). */
  short: string;
  url: string;
  phones: Phone[];
}

/** One sign a business must post, with its rule. */
export interface Posting {
  id: string;
  /** Plain words: what goes up, and where. */
  what: string;
  /** Who it applies to ("every food business", "places that sell alcohol by the drink"). */
  who: string;
  source: Source;
  /** Only in these departments (e.g. the SNHD grade card). Absent = statewide. */
  departments?: string[];
}

/** How inspections are scored and shown to the public. */
export interface InspectionScoring {
  /** "letter" = A/B/C card, "pass-fail" = pass/conditional/fail, "none-found" = no public score in the rules read. */
  system: "letter" | "pass-fail" | "none-found";
  /** Plain line for owners ("A = 0–10 demerits …"). */
  text: string;
  /** Where the public can look results up, when the department says so. */
  lookupUrl?: string;
  source: Source;
  departments?: string[];
}

/** The plain-words fragments a state needs; each is a whole phrase, never glued from parts that change meaning. */
export interface StateCopy {
  /** "Showing Utah rules." */
  showing: string;
  /** "Not in Utah? Pick your state." */
  notHere: string;
  /** "Utah law" — the label of the state layer in a two-layer line ("Utah law: …. Salt Lake County: …"). */
  lawLabel: string;
  /** "same as Utah law" (a county that matches the state). */
  sameAsState: string;
  /** One line naming the state's food code ("Utah follows the 2022 FDA Food Code (Utah Admin. Code R392-100)."). */
  foodCodeLine: string;
  /** Left-out / board-clean verdict state line (leftout-states.ts stateNote). */
  verdictLine: string;
}

/**
 * One FDA Food Code section's place in a state's own code (fix/one-truth: the binder's rule index cites the place's code).
 * `quote` is verbatim in the state's saved excerpts file; `same` is false where the state's text requires something else, and
 * `note` says what, in plain words.
 */
export interface FcIndexEntry { section: string; same: boolean; note?: string; quote?: string }
/** A state's index research (states/<xx>-fcindex.ts, generated). `authorities` are the health authorities whose own code
 *  replaces or adds to the state's (NYC Health Code Article 81; SNHD and Northern Nevada Public Health's regulations). */
export interface FcIndexData {
  excerpts: string;
  state: Record<string, FcIndexEntry>;
  authorities: Record<string, Record<string, FcIndexEntry>>;
  /** Read and not found in the state's code: the FDA line there is our advice, not law. Value: what was searched. */
  absent: Record<string, string>;
  /** Not read far enough to say: shown as "state section not mapped yet: confirm with <authority>". */
  unmapped: Record<string, string>;
  confirmByHand: string[];
}

export interface FoodCode {
  /** FDA Food Code edition the state's rules follow (FDA adoption report Table 7 for the state agency). */
  edition: "2022" | "2017" | "2013" | "2009" | "2005" | "none";
  /** The rule that adopts or writes it ("Utah Admin. Code R392-100", "NAC chapter 446"). */
  rule: string;
  /** How a Food Code section is cited as adopted here ("R392-100-4(1)(b)"); absent = the state writes its own text. */
  adoptsByReference?: string;
  /** Plain line: what the state rule is and how local rules relate to it. */
  text: string;
  source: Source;
  /** Food Code sections whose state text we read and mapped: Food Code section → the state's section. */
  mapped: Record<string, { section: string; same: boolean; note?: string }>;
}

export interface WorkplaceSafety {
  plan: "state" | "federal";
  agency: Agency;
  /** The reporting rule in plain words ("within 8 hours …"), without the phone (oshaAdviceLine adds it). */
  reportLine: string;
  /** The same rule as a clause in "Asked to: …" records ("the person in charge tells Utah OSHA within 8 hours"). */
  askClause: string;
  citation: string;
  source: Source;
}

/** A state rule layer for a requirement or setup-step id (requirements.ts / setup.ts): replaces the Utah text for this state. */
export interface StateRuleLayer {
  title?: string;
  text: string;
  citation: string;
  url: string;
  lastChecked: IsoDate;
}

export interface StatePack {
  code: UsState;
  name: string;
  /** URL segment for the state's public pages ("utah", "nevada"); Utah's pages stay at the site root. */
  slug: string;
  /** Default time zone for a new place (the state's main one). */
  timezone: string;
  foodCode: FoodCode;
  /** The state food agency, and the office named when a county's own department is unknown. */
  stateAgency: Agency;
  /** Every county (and independent city) in the state, A to Z: the list a place's county is validated against. */
  counties: readonly string[];
  /** Permit types this pack's rules describe (setup offers only these). */
  permits: readonly PermitType[];
  foodHandler: Fact<{ text: string; deadline?: string; departments?: string[] }>;
  manager: Fact<{ text: string }>;
  illnessReporting: Fact<{ text: string; within?: string }>;
  workplace: WorkplaceSafety;
  mobile: Fact<{ text: string }>;
  cottageFood: Fact<{ text: string }>;
  homeKitchen: Fact<{ text: string }>;
  postings: Posting[];
  inspections: InspectionScoring[];
  /** Requirement / setup-step ids whose state layer this pack replaces; ids it lists as `null` don't apply in this state. */
  rules: Record<string, StateRuleLayer | null>;
  copy: StateCopy;
  lastChecked: IsoDate;
  /** The day the OWNER reviewed this state's pages and set it at publish. Absent until then; pages say "Last reviewed" only when it is set (an agent's source check is "Sources checked"). */
  ownerReviewed?: IsoDate;
  /** The research file for this pack (docs/source/research/…). */
  research: string;
}

/** A fact's text when verified, else undefined (callers show the unknown line instead of a guess). */
export const factText = <T extends { text: string }>(f: Fact<T>): string | undefined => (f.status === "verified" ? f.text : undefined);

/**
 * One module's state words (the migration convention, docs/STATES.md): Utah's live in states/ut/<module>.ts as a plain object;
 * every other state's file has the same keys, each either its own verified words or null ("no verified rule here": the module
 * leaves the line out or says to ask the health department, never Utah's words with another name).
 */
export type StateWords<T> = { readonly [K in keyof T]: Widen<T[K]> | null };
/**
 * A Utah `as const` slot widened to its kind, so another state can say its own words in the same shape: a single string or
 * number literal becomes string or number. Objects and arrays keep Utah's declared type (declare it in the Utah file when the
 * slot is an object another state fills).
 */
type IsUnion<T, U = T> = T extends unknown ? ([U] extends [T] ? false : true) : never;
export type Widen<T> =
  T extends string ? (string extends T ? T : true extends IsUnion<T> ? T : string)
  : T extends number ? (number extends T ? T : true extends IsUnion<T> ? T : number)
  : T;
